import React, { useState, useRef, useEffect } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { $getRoot, $insertNodes, $isElementNode, LexicalEditor, LexicalNode, $isTextNode, EditorState } from 'lexical';
import { $generateNodesFromDOM, $generateHtmlFromNodes } from '@lexical/html';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
// lexical plugins
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
// lexical playground plugins
import TabFocusPlugin from './plugins/_lexical/TabFocusPlugin';
import ExcalidrawPlugin from './plugins/_lexical/ExcalidrawPlugin';
import TableCellResizerPlugin from './plugins/_lexical/TableCellResizerPlugin';
import FloatingLinkEditorPlugin from './plugins/_lexical/FloatingLinkEditorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/_lexical/FloatingTextFormatToolbarPlugin';
// custom plugins
import ListPlugin from './plugins/ListPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import PhxListVariantPropagationPlugin from './plugins/PhxListVariantPropagationPlugin';
import TableCellActionMenuPlugin from './plugins/TableActionMenuPlugin';
import TextEntitiesPlugin from './plugins/TextEntityPlugin';
import TextEntityTooltipPlugin from './plugins/TextEntityPlugin/TextEntityTooltipPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import TableHoverActionsPlugin from './plugins/TableHoverActionPlugin';
import PhxTrackChangesPlugin from './plugins/PhxTrackChangesPlugin';
import OnPastePluginTC from './plugins/OnPastePluginTC';
import OnPastePlugin from './plugins/OnPastePlugin';
import InlineImagePlugin from './plugins/InlineImagePlugin';
import ImagesPlugin from './plugins/ImagePlugin';
import CrossReferencePlugin from './plugins/CrossReferencePlugin';
import TextColorPlugin from './plugins/ColorPlugin';

// editor contexts
import { ContentEditorTextEntitiesProvider } from './context/ContentEditorTextEntitiesContext';
import { SharedTrackChangesContext, useTrackChanges } from './context/SharedTrackChangesContext';
// editor nodes
import PhoenixNodes from './nodes/PhoenixNodes';
import { $createTextEntityNode, $isTextEntityNode } from './nodes/PhxTextEntityNode';
import { $createImageNode } from './nodes/ImageNode';
import { $createParagraphNode } from 'lexical';
import { $isListNode, getPhxVariantFromDataListType } from './nodes/PhxListNode';
import { EditorPlaceholder, MuiContentEditable } from './styles';
import 'components/ContentEditorShared/styles.css';
import { ProfileResponse } from 'types/profile_response';

import contentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import PhxEditorTheme from 'components/ContentEditorShared/themes/PhxEditorTheme';
import type { CrossReference } from 'views/App/Portal/Policy/_types/manuals/draft';
import type { TextEntity } from 'views/App/Portal/Policy/DraftManuals/utils/textEntityProcessor';

type ContentEditorProps = {
  id: string;
  contentHTML: string;
  onSave?: (contentHTML: string) => void;
  isEditable?: boolean;
  username?: string;
  variant?: 'minimal' | 'policy';
  // Feature flags
  trackChangesEnabled?: boolean;
  textEntitiesEnabled?: boolean;
  crossReferencesEnabled?: boolean;
  imagesEnabled?: boolean;
  attachmentsEnabled?: boolean;
  excalidrawEnabled?: boolean;
  showToolbar?: boolean;
  // Additional props
  manualId?: string;
  crossReferences?: CrossReference[];
  autoFocus?: boolean;
  maxHeight?: string;
  // Attachment handling - decoupled from DraftManuals
  onSelectAttachments?: (selectedAttachments: { id: string; embedded: boolean }[]) => void;
  AttachmentLibraryComponent?: React.ComponentType<{
    open: boolean;
    onCancel: () => void;
    selectMode?: boolean;
    onSelectAttachments?: (selected: any[]) => void;
  }>;
  /** Manual text entities (GIQ + custom) with resolved values; enables hover tooltips when combined with textEntitiesEnabled */
  textEntities?: TextEntity[];
};

const PastePluginWrapper = ({ username }: { username: string }) => {
  const { trackChangesState } = useTrackChanges();
  return trackChangesState ? <OnPastePluginTC username={username} /> : <OnPastePlugin />;
};

export default function ContentEditor({
  id,
  contentHTML,
  onSave,
  isEditable = false,
  variant = 'policy',
  trackChangesEnabled = false,
  textEntitiesEnabled = false,
  crossReferencesEnabled = false,
  imagesEnabled = false,
  attachmentsEnabled = false,
  excalidrawEnabled = true,
  showToolbar = true,
  manualId,
  crossReferences,
  autoFocus = false,
  maxHeight,
  onSelectAttachments,
  AttachmentLibraryComponent,
  textEntities,
}: ContentEditorProps) {
  const getCurrentUserData = () => {
    try {
      const userData = (JSON.parse(localStorage.getItem('phx-currentProfile') || '{}') as ProfileResponse)?.user;
      const username =
        userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : 'Unknown User';
      return username;
    } catch (error) {
      console.error('Error accessing user profile:', error);
      return 'Unknown User';
    }
  };

  function onChange(editorState: EditorState, editor: LexicalEditor) {
    editor.read(() => {
      const content = $generateHtmlFromNodes(editor);
      if (onSave) onSave(content);
    });
  }

  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | undefined>();
  const isSmallWidthViewport = useMediaQuery('(max-width: 1025px)');
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
  const [isNewLink, setIsNewLink] = useState(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const normalizeCrossReferenceAnchors = (content: Document, crossRefs?: CrossReference[]): void => {
    const anchors = content.querySelectorAll('a[href]');
    // Note: we intentionally do not depend on knownIds to strip ids, to avoid removing legitimate ids
    anchors.forEach((anchor) => {
      const href = anchor.getAttribute('href') || '';
      const isXref = /^xref:\/\//i.test(href);
      if (!isXref) return;

      // Ensure the expected class is present so importer recognizes cross-refs
      anchor.classList.add('phx-cross-ref-link');

      // Ensure the id matches crossReferences when available; otherwise strip incorrect/synthetic ids
      const hrefLower = href.toLowerCase();
      const matched = (crossRefs || []).find((cr) => (cr.contentUri || '').toLowerCase() === hrefLower);
      if (matched?.id) {
        anchor.setAttribute('id', matched.id);
      } else {
        const currentId = anchor.getAttribute('id') || '';
        if (currentId) {
          const looksSynthetic = currentId.startsWith('xref-');
          if (looksSynthetic) {
            anchor.removeAttribute('id');
          }
        }
      }
    });
  };

  const findTextNodesWithContent = (root: LexicalNode, textContent: string): LexicalNode[] => {
    const matches: LexicalNode[] = [];

    const traverse = (node: LexicalNode) => {
      // Skip PhxTextEntityNode - they're already properly imported via importDOM with formatting preserved
      if ($isTextEntityNode(node)) {
        return;
      }
      if ($isTextNode(node) && node.getTextContent() === textContent) {
        matches.push(node);
      } else if ($isElementNode(node)) {
        node.getChildren().forEach(traverse);
      }
    };

    traverse(root);
    return matches;
  };

  const replaceTextNodesWithTextEntities = (
    textNodes: LexicalNode[],
    entityData: { id: string; description: string; text: string },
  ) => {
    textNodes.forEach((textNode) => {
      if ($isTextNode(textNode)) {
        const textContent = textNode.getTextContent();
        if (textContent === entityData.text) {
          const textEntityNode = $createTextEntityNode(textContent, {
            id: entityData.id,
            description: entityData.description,
          });
          // Preserve format from the original text node
          const format = textNode.getFormat();
          if (format) {
            textEntityNode.setFormat(format);
          }
          textNode.replace(textEntityNode);
        }
      }
    });
  };

  const customParseEditorState = (editor: LexicalEditor) => {
    if (!contentHTML) {
      return;
    }

    const parser = new DOMParser();
    const processedContent = contentConversionHelper.contentForEditorSmart(contentHTML);
    const content = parser.parseFromString(processedContent, 'text/html');
    normalizeCrossReferenceAnchors(content, crossReferences);

    // First pass: extract information about images before DOM conversion
    const processedImages = new Map<
      string,
      {
        src: string;
        alt: string;
        width: number | 'auto';
        height: number | 'auto';
        manualId?: string;
        attachmentId?: string;
        position: number;
      }
    >();

    const imageElements = content.querySelectorAll('img');
    imageElements.forEach((img, index) => {
      const src = img.getAttribute('src') || '';
      if (!src || processedImages.has(src)) return;

      const alt = img.getAttribute('alt') || '';
      const width = img.getAttribute('width') ? parseInt(img.getAttribute('width') || '0', 10) : 'auto';
      const height = img.getAttribute('height') ? parseInt(img.getAttribute('height') || '0', 10) : 'auto';

      // Extract manualId and attachmentId from the URL if it's from S3
      let manualId: string | undefined;
      let attachmentId: string | undefined;

      try {
        const s3UrlPattern = /manuals\/([^\/]+)\/attachments\/([^\/\?]+)/;
        const matches = src.match(s3UrlPattern);

        if (matches && matches.length === 3) {
          manualId = matches[1];
          attachmentId = matches[2];
        }
      } catch (error) {
        console.error('Error parsing image URL:', error);
      }

      processedImages.set(src, {
        src,
        alt,
        width: typeof width === 'number' ? width : 300,
        height: typeof height === 'number' ? height : 200,
        manualId,
        attachmentId,
        position: index,
      });

      // Replace img with a placeholder that will be removed after DOM conversion
      const placeholder = content.createElement('span');
      placeholder.setAttribute('data-image-placeholder', src);
      placeholder.setAttribute('data-image-position', index.toString());
      placeholder.textContent = ''; // Empty placeholder
      img.parentNode?.replaceChild(placeholder, img);
    });

    // Extract text entities
    const textEntities = content.querySelectorAll('.text-entity');
    const textEntityData: Array<{ id: string; description: string; text: string }> = [];

    Array.from(textEntities).forEach((entity) => {
      try {
        const dataAttr = entity.getAttribute('data-textentity-data');
        if (!dataAttr) {
          console.warn('Text entity missing data attribute:', entity);
          return;
        }

        const entityData = JSON.parse(dataAttr);
        const text = entity.textContent || '';

        textEntityData.push({
          id: entityData.id,
          description: entityData.description,
          text,
        });
      } catch (e) {
        console.error('Error processing text entity:', e);
      }
    });

    // Collect root lists (and their data-list-type / inline list-style-type) so we can assign variant and preserve style
    const listElements = Array.from(content.querySelectorAll('ol, ul')).filter(
      (el) => (el as Element).closest('ol, ul') === el
    );
    const listTypeAttrs = listElements.map((el) => (el as Element).getAttribute('data-list-type'));
    const listStyleTypes = listElements.map((el) => (el as HTMLElement).style?.listStyleType || null);

    editor.update(() => {
      // Clear and insert the base content
      $getRoot().clear();
      const contentNodes = $generateNodesFromDOM(editor, content);

      $getRoot().select();
      $insertNodes(contentNodes);

      const root = $getRoot();
      const listNodes: LexicalNode[] = [];
      const walk = (node: LexicalNode) => {
        if ($isListNode(node)) listNodes.push(node);
        if ($isElementNode(node)) node.getChildren().forEach(walk);
      };
      root.getChildren().forEach(walk);
      // Only root list nodes carry __phxVariant; assign in document order
      const rootListNodes = listNodes.filter((node) => {
        let current = node.getParent();
        while (current) {
          if ($isListNode(current)) return false;
          current = current.getParent();
        }
        return true;
      });
      rootListNodes.forEach((node, i) => {
        const attr = listTypeAttrs[i] ?? null;
        const variant = getPhxVariantFromDataListType(attr);
        const listStyleType = listStyleTypes[i] ?? null;
        if (variant) {
          (node as any).__phxVariant = variant;
        } else {
          // Lists without a Phoenix data-list-type are treated as custom so
          // that any inline list styles are preserved.
          (node as any).__phxVariant = 'custom';
        }
        if (listStyleType) {
          (node as any).__phxListStyleType = listStyleType;
        }
      });

      // Process text entities
      textEntityData.forEach((entityData) => {
        const matchingTextNodes = findTextNodesWithContent(root, entityData.text);
        replaceTextNodesWithTextEntities(matchingTextNodes, entityData);
      });

      // Second pass: add all image nodes at their original positions
      if (processedImages.size > 0) {
        const root = $getRoot();
        const children = root.getChildren();

        const imageNodes = Array.from(processedImages.entries())
          .sort(([, a], [, b]) => a.position - b.position)
          .map(([srcKey, imageData]) => {
            const imageNode = $createImageNode({
              src: imageData.src,
              altText: imageData.alt,
              width: imageData.width,
              height: imageData.height,
              manualId: imageData.manualId,
              attachmentId: imageData.attachmentId,
            });
            return imageNode;
          });

        imageNodes.forEach((imageNode, index) => {
          const paragraphNode = $createParagraphNode();
          paragraphNode.append(imageNode);

          if (index < children.length) {
            children[index].insertAfter(paragraphNode);
          } else {
            root.append(paragraphNode);
          }
        });
      }
    });
  };

  const renderEditorContent = () => (
    <>
      {isEditable && showToolbar && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: '#FFF',
          }}
        >
          <ToolbarPlugin
            setIsLinkEditMode={setIsLinkEditMode}
            setIsNewLink={setIsNewLink}
            textEntityEnabled={textEntitiesEnabled}
            excalidrawEnabled={excalidrawEnabled}
            crossReferencesEnabled={crossReferencesEnabled}
            attachmentsEnabled={attachmentsEnabled}
            manualId={manualId}
            onSelectAttachments={onSelectAttachments}
            AttachmentLibraryComponent={AttachmentLibraryComponent}
            variant={variant}
          />
        </Box>
      )}
      <Box
        className='editor-container'
        sx={{
          position: 'relative',
          background: '#FFF',
          pt: 2,
          '& .editor-scroller': {
            minHeight: variant === 'minimal' ? '50px' : '150px',
            ...(maxHeight != null && { maxHeight }),
            ...(maxHeight != null && { overflowAnchor: 'auto' }),
          },
          ...(variant === 'minimal' && {
            '& [contenteditable]': { minHeight: '50px' },
          }),
        }}
      >
        <RichTextPlugin
          contentEditable={
            <div className='editor-scroller'>
              <div
                className='editor'
                ref={onRef}
              >
                <MuiContentEditable />
              </div>
            </div>
          }
          placeholder={<EditorPlaceholder>{isEditable && 'Enter some text...'}</EditorPlaceholder>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        {crossReferencesEnabled && <CrossReferencePlugin />}
        <CheckListPlugin />
        <ClearEditorPlugin />
        {excalidrawEnabled && <ExcalidrawPlugin />}
        <HistoryPlugin />
        <HorizontalRulePlugin />
        <ListPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <PhxListVariantPropagationPlugin />
        <LinkPlugin />
        <OnChangePlugin onChange={onChange} />
        <TabFocusPlugin />
        <TabIndentationPlugin />
        <TablePlugin
          hasCellMerge={true}
          hasCellBackgroundColor={true}
        />
        <TableCellResizerPlugin />
        <TableHoverActionsPlugin anchorElem={floatingAnchorElem} />
        {textEntitiesEnabled && <TextEntitiesPlugin manualId={manualId} />}
        <TextEntityTooltipPlugin textEntitiesEnabled={textEntitiesEnabled} />
        <TextColorPlugin />
        {imagesEnabled && (
          <>
            <ImagesPlugin />
            <InlineImagePlugin />
          </>
        )}
        {/* Conditionally render track changes related plugins */}
        {trackChangesEnabled ? (
          <>
            <PhxTrackChangesPlugin username={getCurrentUserData()} />
            <PastePluginWrapper username={getCurrentUserData()} />
          </>
        ) : (
          <OnPastePlugin />
        )}
        {floatingAnchorElem && !isSmallWidthViewport && (
          <>
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
              isNewLink={isNewLink}
              setIsNewLink={setIsNewLink}
            />
            <TableCellActionMenuPlugin
              anchorElem={floatingAnchorElem}
              cellMerge={true}
            />
            <FloatingTextFormatToolbarPlugin
              anchorElem={floatingAnchorElem}
              setIsLinkEditMode={setIsLinkEditMode}
              setIsNewLink={setIsNewLink}
            />
          </>
        )}
      </Box>
    </>
  );

  return (
    <Box
      className={`editor-shell ${isEditable ? 'editor-shell--editable' : ''}${
        variant === 'minimal' ? ' editor-shell--minimal' : ''
      }${variant === 'minimal' && maxHeight != null ? ' editor-shell--minimal-capped' : ''}`}
    >
      <LexicalComposer
        initialConfig={{
          namespace: id,
          nodes: PhoenixNodes,
          theme: PhxEditorTheme,
          editable: isEditable,
          onError: (error) => {
            console.error(error);
          },
          editorState: customParseEditorState,
        }}
      >
        <ContentEditorTextEntitiesProvider textEntities={textEntities}>
          {trackChangesEnabled ? (
            <SharedTrackChangesContext>{renderEditorContent()}</SharedTrackChangesContext>
          ) : (
            renderEditorContent()
          )}
        </ContentEditorTextEntitiesProvider>
      </LexicalComposer>
    </Box>
  );
}
